/**
 * CitationHistorySection — renders live LLM citation data from citation_history.
 *
 * Five rendering states:
 *   A — sweep in progress, no data yet        (spinner + polling)
 *   B — no data, no sweep                     (run-audit prompt)
 *   C — data present, 0 citations             (baseline framing)
 *   D — data present, citations exist         (success view)
 *   E — sweep in progress, data already exists (existing data + update banner)
 *
 * Polling: 30-second interval while sweepInProgress is true.
 * Timeout: polling stops after 10 minutes regardless.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../api';
import type { CitationHistoryResponse, PlatformSummary, RecentQuery } from '../types';

const POLL_INTERVAL_MS  = 30_000;
const POLL_TIMEOUT_MS   = 10 * 60_000;

interface Props {
  domainId: string;
  onTriggerAudit?: () => void;
  /** Increment to trigger an immediate re-fetch + polling restart (e.g. after an audit is queued). */
  refreshKey?: number;
}

// ── Sub-components ─────────────────────────────────────────────────────────

function PlatformTable({ platforms }: { platforms: PlatformSummary[] }) {
  if (platforms.length === 0) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-brand-ink/8">
            <th className="text-left py-2 pr-4 font-medium text-brand-ink/50">Platform</th>
            <th className="text-right py-2 pr-4 font-medium text-brand-ink/50">Queries</th>
            <th className="text-right py-2 font-medium text-brand-ink/50">Citations</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-brand-ink/5">
          {platforms.map((p) => (
            <tr key={p.platformId}>
              <td className="py-2 pr-4 text-brand-ink/80">{p.platformDisplayName}</td>
              <td className="py-2 pr-4 text-right text-brand-ink/60">{p.totalQueries}</td>
              <td className="py-2 text-right">
                {p.totalCited > 0 ? (
                  <span className="text-brand-green font-medium">
                    {p.totalCited} ({Math.round(p.citationRate * 100)}%)
                  </span>
                ) : (
                  <span className="text-brand-ink/40">0</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function QueryList({
  queries,
  showCited,
  maxVisible = 8,
}: {
  queries: RecentQuery[];
  showCited: boolean;
  maxVisible?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const filtered = showCited ? queries.filter((q) => q.cited) : queries;
  const visible  = expanded ? filtered : filtered.slice(0, maxVisible);
  if (filtered.length === 0) return null;

  return (
    <div className="space-y-1.5">
      {visible.map((q, i) => (
        <div
          key={i}
          className={`flex items-start gap-3 py-2 px-3 rounded-lg text-xs ${
            q.cited ? 'bg-green-50/50 border border-brand-green/15' : 'bg-brand-ink/[0.02] border border-brand-ink/6'
          }`}
        >
          <span className={`shrink-0 mt-0.5 font-semibold ${q.cited ? 'text-brand-green' : 'text-brand-ink/30'}`}>
            {q.cited ? '✓' : '–'}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-brand-ink/70 leading-snug italic">
              &ldquo;{q.query ?? '(query not recorded)'}&rdquo;
            </p>
            <p className="text-brand-ink/35 mt-0.5">
              {q.platformId}
              {q.citationPosition !== null ? ` · position ${q.citationPosition}` : ''}
            </p>
            {q.responseExcerpt && (
              <p className="text-brand-ink/50 mt-1 font-mono text-[11px] leading-relaxed">
                {q.responseExcerpt}
              </p>
            )}
          </div>
        </div>
      ))}
      {filtered.length > maxVisible && (
        <button
          onClick={() => setExpanded((e) => !e)}
          className="text-xs text-brand-green underline mt-1"
        >
          {expanded ? 'Show fewer' : `Show all ${filtered.length} queries`}
        </button>
      )}
    </div>
  );
}

// ── State A: sweep in progress, no data ────────────────────────────────────

function StateA({ lastPolledAt }: { lastPolledAt: Date | null }) {
  return (
    <div className="p-5 rounded-xl border border-brand-ink/8 bg-brand-paper/50">
      <div className="flex items-center gap-3 mb-3">
        <svg className="animate-spin h-4 w-4 text-brand-green shrink-0" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <p className="text-sm font-medium text-brand-ink">Running AI citation interrogations…</p>
      </div>
      <p className="text-sm text-brand-ink/60 leading-relaxed mb-3">
        We&rsquo;re querying AI platforms with questions about your category and location.
        This usually takes 2–5 minutes. The page will refresh automatically when results are ready.
      </p>
      {lastPolledAt && (
        <p className="text-xs text-brand-ink/35">
          Last checked: {lastPolledAt.toLocaleTimeString()}
        </p>
      )}
    </div>
  );
}

// ── State B: no data, no sweep ─────────────────────────────────────────────

function StateB({ onTriggerAudit }: { onTriggerAudit?: (() => void) | undefined }) {
  return (
    <div className="p-5 rounded-xl border border-brand-ink/8 bg-brand-paper/50">
      <p className="text-sm text-brand-ink/60 leading-relaxed">
        No AI citation data yet. Run an audit to start measuring your visibility across AI platforms.
      </p>
      {onTriggerAudit && (
        <button
          onClick={onTriggerAudit}
          className="mt-3 text-xs font-medium text-white bg-brand-green hover:bg-brand-green-dark px-3 py-1.5 rounded-lg transition-colors"
        >
          Run audit
        </button>
      )}
    </div>
  );
}

// ── Corpus provenance ──────────────────────────────────────────────────────

/**
 * Tells the user how the questions were chosen.
 *
 * A fallback corpus is generated from templates rather than real keyword-volume
 * data, so its citation rate is a weaker signal. Previously both were presented
 * identically, which made a degraded sweep indistinguishable from a good one.
 * Nothing is shown for a healthy 'dataforseo' corpus — this is an exception
 * indicator, not a badge.
 */
function CorpusProvenanceNotice({
  corpusSource,
}: {
  corpusSource: CitationHistoryResponse['meta']['corpusSource'];
}) {
  if (corpusSource === 'dataforseo' || corpusSource === null) return null;

  return (
    <div className="p-3 rounded-lg border border-amber-300/40 bg-amber-50/40 flex gap-2.5 items-start">
      <svg
        width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2"
        className="text-amber-600 mt-0.5 shrink-0"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      </svg>
      <p className="text-xs text-brand-ink/70 leading-relaxed">
        <strong className="text-brand-ink/85">Estimated questions.</strong>{' '}
        Live search-volume data was unavailable for this sweep, so these questions were
        generated from templates for your business type. They are representative rather
        than measured — treat this result as indicative until the next sweep.
      </p>
    </div>
  );
}

// ── State C: data present, 0 citations ────────────────────────────────────

function StateC({
  data,
  lastPolledAt,
  onRefresh,
}: {
  data: CitationHistoryResponse;
  lastPolledAt: Date | null;
  onRefresh: () => void;
}) {
  const [queriesOpen, setQueriesOpen] = useState(false);
  const platformCount = data.platforms.length;
  const queryCount    = data.meta.totalQueries;

  return (
    <div className="space-y-4">
      <div className="p-5 rounded-xl border border-brand-ink/8 bg-brand-paper/30">
        <p className="text-xs font-semibold text-brand-green uppercase tracking-wide mb-1">Baseline measured.</p>
        <p className="text-sm text-brand-ink/80 leading-relaxed">
          We asked {platformCount} AI platform{platformCount !== 1 ? 's' : ''} {queryCount} question{queryCount !== 1 ? 's' : ''} about your category in your area.
          Currently you appear in <strong>0 results</strong>. <strong>This is your starting point.</strong>
        </p>
        <p className="text-sm text-brand-ink/55 mt-2 leading-relaxed">
          As you implement the schema fixes and content recommendations from your audits,
          this number will grow over time.
        </p>
      </div>

      <CorpusProvenanceNotice corpusSource={data.meta.corpusSource} />

      <PlatformTable platforms={data.platforms} />

      <div>
        <button
          onClick={() => setQueriesOpen((o) => !o)}
          className="text-xs text-brand-ink/50 hover:text-brand-ink flex items-center gap-1 mb-2 transition-colors"
        >
          <svg
            width="12" height="12" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2"
            className={`transition-transform ${queriesOpen ? 'rotate-90' : ''}`}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" />
          </svg>
          {queriesOpen ? 'Hide' : 'Show'} sample queries asked ({data.recentQueries.length})
        </button>
        {queriesOpen && (
          <QueryList queries={data.recentQueries} showCited={false} maxVisible={6} />
        )}
      </div>

      <Footer lastSweepAt={data.meta.lastSweepAt} lastPolledAt={lastPolledAt} onRefresh={onRefresh} />
    </div>
  );
}

// ── State D: citations exist ───────────────────────────────────────────────

function StateD({
  data,
  lastPolledAt,
  onRefresh,
}: {
  data: CitationHistoryResponse;
  lastPolledAt: Date | null;
  onRefresh: () => void;
}) {
  const [queriesOpen, setQueriesOpen] = useState(false);
  const rate = Math.round(data.meta.citationRate * 100);
  return (
    <div className="space-y-4">
      <div className="p-5 rounded-xl border border-brand-green/20 bg-green-50/30">
        <p className="text-xs font-semibold text-brand-green uppercase tracking-wide mb-1">
          Cited {data.meta.totalCited} time{data.meta.totalCited !== 1 ? 's' : ''} in {data.meta.totalQueries} queries ({rate}%)
        </p>
        <p className="text-sm text-brand-ink/60 leading-relaxed">
          AI platforms are starting to reference your business in relevant queries.
        </p>
      </div>

      <CorpusProvenanceNotice corpusSource={data.meta.corpusSource} />

      <PlatformTable platforms={data.platforms} />

      <div>
        <p className="text-xs font-semibold text-brand-ink/50 uppercase tracking-wide mb-2">
          Cited queries
        </p>
        <QueryList queries={data.recentQueries} showCited={true} maxVisible={6} />
      </div>

      {/*
        The full corpus stays reachable once citations exist. Previously this
        state rendered cited queries only, so a single brand-query citation made
        it look like that was the only thing ever asked — hiding the other 22.
      */}
      <div>
        <button
          onClick={() => setQueriesOpen((o) => !o)}
          className="text-xs text-brand-ink/50 hover:text-brand-ink flex items-center gap-1 mb-2 transition-colors"
        >
          <svg
            width="12" height="12" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2"
            className={`transition-transform ${queriesOpen ? 'rotate-90' : ''}`}
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" />
          </svg>
          {queriesOpen ? 'Hide' : 'Show'} all questions asked ({data.recentQueries.length})
        </button>
        {queriesOpen && (
          <QueryList queries={data.recentQueries} showCited={false} maxVisible={8} />
        )}
      </div>

      <Footer lastSweepAt={data.meta.lastSweepAt} lastPolledAt={lastPolledAt} onRefresh={onRefresh} />
    </div>
  );
}

// ── Footer ─────────────────────────────────────────────────────────────────

function Footer({
  lastSweepAt,
  lastPolledAt,
  onRefresh,
}: {
  lastSweepAt: string | null;
  lastPolledAt: Date | null;
  onRefresh: () => void;
}) {
  return (
    <div className="flex items-center justify-between text-xs text-brand-ink/35 pt-2 border-t border-brand-ink/6">
      <span>
        {lastSweepAt
          ? `Last swept: ${new Date(lastSweepAt).toLocaleString()}`
          : lastPolledAt
            ? `Checked: ${lastPolledAt.toLocaleTimeString()}`
            : null}
      </span>
      <button
        onClick={onRefresh}
        className="text-brand-green/70 hover:text-brand-green underline transition-colors"
      >
        Refresh
      </button>
    </div>
  );
}

// ── Main export ────────────────────────────────────────────────────────────

export function CitationHistorySection({ domainId, onTriggerAudit, refreshKey = 0 }: Props) {
  const [data, setData]         = useState<CitationHistoryResponse | null | undefined>(undefined);
  const [error, setError]       = useState<string | null>(null);
  const [lastPolledAt, setLastPolledAt] = useState<Date | null>(null);

  const pollingStartRef = useRef<number | null>(null);
  const intervalRef     = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const result = await api.domains.getCitationHistory(domainId);
      setData(result);
      setLastPolledAt(new Date());
      setError(null);
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load citation data');
      return null;
    }
  }, [domainId]);

  // Stop polling helper
  const stopPolling = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Start/manage polling when sweep is in progress
  useEffect(() => {
    let cancelled = false;

    void fetchData().then((initial) => {
      if (cancelled) return;
      if (!initial?.meta.sweepInProgress) return;

      // Mark polling start time for the 10-minute timeout
      pollingStartRef.current = Date.now();

      intervalRef.current = setInterval(async () => {
        if (cancelled) return;

        // Enforce 10-minute timeout
        if (
          pollingStartRef.current !== null &&
          Date.now() - pollingStartRef.current >= POLL_TIMEOUT_MS
        ) {
          stopPolling();
          return;
        }

        const updated = await fetchData();
        if (!updated) return;

        // Stop once sweep is done
        if (!updated.meta.sweepInProgress) {
          stopPolling();
        }
      }, POLL_INTERVAL_MS);
    });

    return () => {
      cancelled = true;
      stopPolling();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId, refreshKey]);

  const handleRefresh = useCallback(async () => {
    stopPolling();
    const updated = await fetchData();
    if (updated?.meta.sweepInProgress) {
      pollingStartRef.current = Date.now();
      intervalRef.current = setInterval(async () => {
        const next = await fetchData();
        if (!next?.meta.sweepInProgress) stopPolling();
      }, POLL_INTERVAL_MS);
    }
  }, [fetchData, stopPolling]);

  // ── Loading ──────────────────────────────────────────────────────────────
  if (data === undefined) {
    return (
      <div className="h-10 flex items-center">
        <div className="h-3 w-40 bg-brand-ink/8 rounded animate-pulse" />
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="p-5 rounded-xl border border-red-100 bg-red-50/30">
        <p className="text-sm text-red-600 mb-2">{error}</p>
        <button
          onClick={handleRefresh}
          className="text-xs font-medium text-white bg-red-500 hover:bg-red-600 px-3 py-1.5 rounded-lg transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const sweepInProgress = data?.meta.sweepInProgress ?? false;
  const totalQueries    = data?.meta.totalQueries ?? 0;
  const totalCited      = data?.meta.totalCited ?? 0;

  // State A: sweep running, no data yet
  if (sweepInProgress && totalQueries === 0) {
    return <StateA lastPolledAt={lastPolledAt} />;
  }

  // State B: no sweep, no data
  if (!sweepInProgress && totalQueries === 0) {
    return <StateB onTriggerAudit={onTriggerAudit} />;
  }

  // State E: sweep running, data already exists — show data + banner
  const banner = sweepInProgress ? (
    <div className="flex items-center gap-2 mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50/40 text-xs text-amber-700">
      <svg className="animate-spin h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
      Updating… new sweep in progress. Results will refresh automatically when complete.
    </div>
  ) : null;

  // State C: data, 0 cited
  if (totalCited === 0 && data) {
    return (
      <>
        {banner}
        <StateC data={data} lastPolledAt={lastPolledAt} onRefresh={handleRefresh} />
      </>
    );
  }

  // State D: data, citations exist
  if (data) {
    return (
      <>
        {banner}
        <StateD data={data} lastPolledAt={lastPolledAt} onRefresh={handleRefresh} />
      </>
    );
  }

  return null;
}
